import type { Skill, SkillFile, SkillTemplate } from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

export function useSkillTemplates(
  options?: Partial<UseQueryOptions<SkillTemplate[], AxiosError>>,
) {
  return useQuery<SkillTemplate[], AxiosError>({
    queryKey: ["skill-templates"],
    queryFn: async () => {
      return await orgAPI.skillTemplates.list();
    },
    ...options,
  });
}

export function useSkillTemplate(
  templateId: string,
  options?: Partial<UseQueryOptions<SkillTemplate, AxiosError>>,
) {
  return useQuery<SkillTemplate, AxiosError>({
    queryKey: ["skill-template", templateId],
    queryFn: async () => {
      return await orgAPI.skillTemplates.getById(templateId);
    },
    enabled: !!templateId,
    ...options,
  });
}

export function useSkillTemplateFiles(
  templateId: string,
  options?: Partial<UseQueryOptions<SkillFile[], AxiosError>>,
) {
  return useQuery<SkillFile[], AxiosError>({
    queryKey: ["skill-template-files", templateId],
    queryFn: async () => {
      return await orgAPI.skillTemplates.listFiles(templateId);
    },
    enabled: !!templateId,
    ...options,
  });
}

/** Copies a template into a new private skill (Manual default). Returns the new skill. */
export function useCopySkillTemplate(
  options?: UseMutationOptions<Skill, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation<Skill, AxiosError, string>({
    mutationFn: async (templateId) => {
      return await orgAPI.skillTemplates.copy(templateId);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      queryClient.invalidateQueries({ queryKey: ["skill-templates"] });
      queryClient.invalidateQueries({
        queryKey: ["skill-template", variables],
      });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

/** Unpublishes (hard-deletes) an organization template. Publisher or org admin only. */
export function useUnpublishSkillTemplate(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: async (templateId) => {
      await orgAPI.skillTemplates.unpublish(templateId);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["skill-templates"] });
      queryClient.removeQueries({ queryKey: ["skill-template", variables] });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}
